<template>
  <div class="v-dialog-card-border fixed-form-card">
    <v-card class="elevation-12" style="position: relative">
      <!-- Overlay de salvamento/carregamento -->
      <v-overlay
        :value="isSaving || isLoading"
        absolute
        color="white"
        opacity="0.8"
        z-index="10"
      >
        <div class="d-flex flex-column align-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
            width="4"
          ></v-progress-circular>
          <span class="text-body-1 mt-3 primary--text font-weight-medium">
            {{ isLoading ? "Carregando consulta..." : "Salvando consulta..." }}
          </span>
        </div>
      </v-overlay>
      <!-- Header -->
      <v-card-title class="ds-dialog-header pa-4">
        <div
          class="d-flex align-center"
          style="width: 100%; position: relative; z-index: 1"
        >
          <!-- AudioRecorder só aparece no modo CREATE -->
          <AudioRecorder
            v-if="!editMode"
            data-tour="ad-gravacao"
            ref="audioRecorder"
            @update:transcription="finalTranscript = $event"
            @update:interimTranscript="interimTranscript = $event"
            @update:recordingTime="recordingTime = $event"
            @update:isRecording="onRecordingChange"
            :initial-commands="voiceCommands"
            :initial-transcript="finalTranscript"
            @update:voiceCommands="voiceCommands = $event"
            @record-command="prepareInstantProposal"
            @recording-stopped="handleRecordingStopped"
            @recording-paused="handleRecordingPaused"
          />
          <span v-else class="ds-dialog-title">Editar Consulta</span>
          <v-spacer></v-spacer>

          <!--
            salvia-861: a porta de entrada dos comandos ditos na consulta.

            Botao, e nao abertura automatica ao salvar: processar os comandos
            custa uma chamada ao assistente por comando, e disparar isso sozinho
            no fim de todo atendimento surpreende o medico e gasta a toa. Aqui
            ele decide quando revisar — e o contador deixa claro que ha algo
            esperando.
          -->
          <v-btn
            v-if="!editMode && voiceCommands.length"
            small
            text
            dark
            class="mr-2"
            @click="goToCommands"
          >
            <v-icon small class="mr-1">mdi-microphone-message</v-icon>
            {{ voiceCommands.length }}
            {{ voiceCommands.length === 1 ? "comando" : "comandos" }}
          </v-btn>

          <div v-if="!editMode" class="d-flex align-center mr-4 timer-badge">
            <v-icon color="white" class="mr-1" small>mdi-timer-outline</v-icon>
            <span class="timer-text">{{ formattedTime }}</span>
          </div>
          <v-btn icon color="white" aria-label="Ver tour" @click="startTour">
            <v-icon>mdi-help-circle-outline</v-icon>
          </v-btn>
          <v-btn icon dark class="close-btn" @click="requestClose">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-divider></v-divider>

      <!--
        salvia-839 — pedido do Vidal (reuniao com as nutricionistas, 03/09).

        Consulta online transcreve o que sai do ALTO-FALANTE: e assim que a voz
        do paciente chega ao microfone. De fone, o microfone so ouve o medico, e
        metade da consulta some da transcricao sem nenhum sinal de erro — o
        audio grava, o texto sai, e so falta um dos dois lados.

        Por isso a tarja aparece ao COMECAR a gravar, e nao ao abrir a consulta:
        e no instante de gravar que a escolha do fone ainda da para desfazer.

        A copy e literal, ditada pelo solicitante. Nao reescrever.
      -->
      <v-alert
        v-if="showHeadphoneWarning"
        type="error"
        dense
        tile
        class="mb-0 aviso-fone"
      >
        <div class="d-flex align-center">
          <span class="aviso-fone__texto">{{ headphoneWarningText }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            aria-label="Dispensar aviso"
            class="aviso-fone__fechar"
            @click="headphoneWarningDismissed = true"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-alert>

      <!-- Content -->
      <v-card-text class="pa-4 form-content">
        <v-form ref="appointmentForm" v-model="formValid">
          <!-- Campo Motivo da Consulta -->
          <!--
            Quando o paciente ja tem consulta agendada para hoje, o motivo vem
            da agenda e o card encolhe para uma linha so: o proprio titulo
            mostra o motivo, sem seletor. Nao havendo agendamento, o card volta
            ao normal e o medico escolhe.
          -->
          <v-card outlined class="ds-section-card mb-4" data-tour="ad-motivo">
            <v-card-title class="ds-section-card-title">
              <v-icon small color="primary" class="mr-2"
                >mdi-clipboard-text-outline</v-icon
              >
              <span>
                {{ reasonCollapsed ? finalReasonValue : "Motivo da Consulta" }}
              </span>
              <!-- SALVIA-929: veio da agenda, mas o agendamento pode estar
                   errado — o lápis reabre o seletor para corrigir. -->
              <template v-if="reasonCollapsed">
                <span class="reason-origin ml-2">do agendamento</span>
                <v-spacer></v-spacer>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      v-bind="attrs"
                      v-on="on"
                      aria-label="Alterar motivo"
                      @click="reasonEditing = true"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Alterar motivo</span>
                </v-tooltip>
              </template>
            </v-card-title>
            <!-- SALVIA-929: observações do agendamento, só para leitura. -->
            <div v-if="scheduledNotes" class="reason-notes">
              <v-icon x-small class="mr-1">mdi-calendar-text-outline</v-icon>
              <span class="reason-notes__label">Obs. do agendamento:</span>
              {{ scheduledNotes }}
            </div>
            <v-card-text v-if="!reasonCollapsed" class="reason-body">
              <v-select
                v-model="selectedReason"
                :items="appointmentTypes"
                item-text="label"
                item-value="value"
                label="Selecione o motivo"
                outlined
                dense
                hide-details="auto"
                :rules="[(v) => !!v || 'Motivo é obrigatório']"
              ></v-select>

              <!-- Campo para especificar "Outra" -->
              <v-text-field
                v-if="selectedReason === 'Outra'"
                v-model="otherReasonText"
                label="Especifique o motivo"
                class="mt-2"
                outlined
                dense
                hide-details="auto"
                placeholder="Digite o motivo da consulta..."
                :rules="[(v) => !!v || 'Por favor, especifique o motivo']"
              ></v-text-field>

              <!-- SALVIA-929 (d): o agendamento continua com o motivo original;
                   aqui só se mostra que ele existia. -->
              <p v-if="reasonChangedFromSchedule" class="reason-original mt-2 mb-0">
                <v-icon x-small class="mr-1">mdi-calendar-check</v-icon>
                Agendado como: <strong>{{ scheduledReasonOriginal }}</strong>
                <a
                  href="#"
                  class="reason-restore ml-2"
                  @click.prevent="restoreScheduledReason"
                >voltar ao agendado</a>
              </p>
            </v-card-text>
          </v-card>

          <!-- Bloco: Anotações da Consulta -->
          <v-card outlined class="ds-section-card mb-4" data-tour="ad-notas">
            <v-card-title class="ds-section-card-title">
              <v-icon small color="primary" class="mr-2"
                >mdi-note-text-outline</v-icon
              >
              <span>Anotações da Consulta</span>
            </v-card-title>
            <v-card-text class="pa-3">
              <!-- As duas colunas têm a mesma altura: a da transcrição (10
                   linhas fixas) manda, e a lista de notas ocupa o que sobra
                   acima do campo de nova nota. -->
              <v-row class="notes-row">
                <!-- Notas Salvas -->
                <v-col cols="12" md="6" class="notes-col">
                  <div class="ds-inline-label col-head mb-2">
                    <v-icon small color="primary" class="mr-2"
                      >mdi-bookmark-check-outline</v-icon
                    >
                    <span>Notas Salvas</span>
                  </div>

                  <!--
                    Propostas ditas por voz (salvia-861, 22/09).

                    Ficam AQUI, e nao so no painel de comandos, por decisao do
                    Ariel: ter que clicar num botao para ver o que ja foi pedido
                    escondia o trabalho. Aparecem ACIMA das notas porque exigem
                    acao — nota salva ja esta resolvida, proposta nao.

                    Nada foi gravado no prontuario: o card aprova ou recusa.
                  -->
                  <div v-if="instantProposals.length" class="mb-2">
                    <ProposalCard
                      v-for="p in instantProposals"
                      :key="p.id"
                      :proposal="p"
                      @update="onInstantProposalUpdate"
                    />
                  </div>
                  <div
                    v-if="preparingProposal"
                    class="text-caption text--secondary mb-2 d-flex align-center"
                  >
                    <v-progress-circular
                      indeterminate
                      size="14"
                      width="2"
                      color="primary"
                      class="mr-2"
                    />
                    Preparando a proposta do que você acabou de ditar…
                  </div>
                  <!--
                    Adiado NÃO é erro: o comando foi ouvido e a varredura do fim
                    da consulta vai pegá-lo. O aviso existe porque o gravador já
                    prometeu "preparando agora" — sem isto, o profissional espera
                    um card que não vem.
                  -->
                  <div
                    v-if="deferredCommands.length"
                    class="text-caption text--secondary mb-2"
                  >
                    <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                    Ainda não há transcrição para basear
                    {{ deferredCommands.length === 1 ? "este comando" : "estes comandos" }}.
                    A salvIA prepara no fim da consulta:
                    <em>{{ deferredCommands.join("; ") }}</em>
                  </div>

                  <div
                    v-if="instantProposalError"
                    class="text-caption error--text mb-2"
                  >
                    <v-icon x-small color="error" class="mr-1"
                      >mdi-alert-circle-outline</v-icon
                    >
                    {{ instantProposalError }} O comando continua na lista e a
                    salvIA tenta de novo no fim da consulta.
                  </div>

                  <div class="notes-panel">
                    <div class="notes-list">
                      <v-list v-if="notes.length > 0" dense class="py-0">
                        <!--
                          SALVIA-881: Nota → Resumo → Conversa. Clicar em
                          qualquer ponto da nota (menos na lixeira) leva à frase
                          do resumo que a contém; sem resumo, ao ponto em que
                          ela entrou na transcrição.
                        -->
                        <v-list-item
                          v-for="note in notes"
                          :key="note.id"
                          class="note-link"
                          :title="noteLinkHint"
                          @click="openNote(note)"
                        >
                          <v-list-item-content>
                            <v-list-item-title class="text-wrap">{{
                              note.content
                            }}</v-list-item-title>
                            <v-list-item-subtitle class="text-caption">{{
                              note.timestamp
                            }}</v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-action class="my-0">
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  x-small
                                  v-bind="attrs"
                                  v-on="on"
                                  aria-label="Excluir nota"
                                  @click.stop="deleteNote(note.id)"
                                  @mousedown.stop
                                >
                                  <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                              </template>
                              <span>Excluir nota</span>
                            </v-tooltip>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list>
                      <p v-else class="notes-empty">Nenhuma anotação ainda.</p>
                    </div>

                    <div class="notes-composer">
                      <v-textarea
                        v-model="noteInput"
                        outlined
                        dense
                        no-resize
                        rows="2"
                        placeholder="Digite uma anotação... (Enter para salvar)"
                        hide-details
                        @keydown.enter.exact.prevent="saveNote"
                      ></v-textarea>
                      <div class="d-flex justify-end mt-2">
                        <v-btn
                          small
                          color="primary"
                          depressed
                          @click="saveNote"
                          :disabled="!noteInput.trim()"
                        >
                          <v-icon small left>mdi-content-save</v-icon>
                          Salvar Nota
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Transcrição -->
                <v-col cols="12" md="6">
                  <div class="d-flex align-center justify-space-between col-head mb-2">
                    <div class="ds-inline-label">
                      <v-icon small color="primary" class="mr-2"
                        >mdi-microphone-outline</v-icon
                      >
                      <span>Transcrição de Áudio</span>
                    </div>
                    <div v-if="finalTranscript || editingTranscript">
                      <!-- Modo edição: Salvar / Cancelar -->
                      <template v-if="editingTranscript">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              x-small
                              icon
                              color="success"
                              v-bind="attrs"
                              v-on="on"
                              @click="saveEditTranscript"
                            >
                              <v-icon small>mdi-check</v-icon>
                            </v-btn>
                          </template>
                          <span>Salvar edição</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              x-small
                              icon
                              v-bind="attrs"
                              v-on="on"
                              @click="cancelEditTranscript"
                            >
                              <v-icon small>mdi-close</v-icon>
                            </v-btn>
                          </template>
                          <span>Cancelar</span>
                        </v-tooltip>
                      </template>
                      <!-- Modo visualização: Editar / Copiar / Baixar -->
                      <template v-else>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              x-small
                              icon
                              v-bind="attrs"
                              v-on="on"
                              :disabled="audioIsRecording"
                              @click="startEditTranscript"
                            >
                              <v-icon small>mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Editar transcrição</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              x-small
                              icon
                              v-bind="attrs"
                              v-on="on"
                              @click="copyTranscription"
                            >
                              <v-icon small>mdi-content-copy</v-icon>
                            </v-btn>
                          </template>
                          <span>Copiar transcrição</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              x-small
                              icon
                              v-bind="attrs"
                              v-on="on"
                              @click="downloadTranscription"
                            >
                              <v-icon small>mdi-download</v-icon>
                            </v-btn>
                          </template>
                          <span>Baixar transcrição</span>
                        </v-tooltip>
                      </template>
                    </div>
                  </div>

                  <!-- Modo edição -->
                  <!-- SALVIA-952: 10 linhas fixas; passou disso, rola por dentro. -->
                  <v-textarea
                    v-if="editingTranscript"
                    v-model="transcriptDraft"
                    class="transcript-edit"
                    outlined
                    dense
                    no-resize
                    rows="10"
                    hide-details
                    placeholder="Edite a transcrição..."
                  ></v-textarea>

                  <div v-else>
                  <!-- SALVIA-881: trecho aberto a partir de uma frase do resumo -->
                  <div v-if="transcriptPassage" class="transcript-passage-bar">
                    <v-icon x-small class="mr-1">mdi-link-variant</v-icon>
                    {{ transcriptPassage.label || "Trecho da frase do resumo destacado abaixo" }}
                    <v-btn x-small text class="ml-1" @click="transcriptPassage = null">
                      Limpar
                    </v-btn>
                  </div>

                  <!-- Modo visualização -->
                  <v-card
                    ref="transcriptBox"
                    outlined
                    class="transcript-box"
                  >
                    <v-card-text
                      v-if="!finalTranscript && !interimTranscript"
                      class="text-center py-4"
                    >
                      <p class="text-caption text--secondary ma-0">
                        {{
                          editMode
                            ? "Transcrição não disponível."
                            : "A transcrição aparecerá aqui enquanto a consulta é transcrita."
                        }}
                      </p>
                    </v-card-text>
                    <v-card-text v-else class="transcription-text">
                      <span v-html="transcriptDisplayHtml"></span>
                      <!-- Fala ainda em reconhecimento: só exibição, não é
                           salva nem vai para o resumo até o navegador fechá-la. -->
                      <span v-if="interimTranscript" class="transcript-interim">{{
                        interimTranscript
                      }}</span>
                      <div
                        v-if="recordingEndTime"
                        class="mt-3 text-caption text--secondary"
                      >
                        <v-divider class="mb-2"></v-divider>
                        <v-icon small color="grey">mdi-clock-outline</v-icon>
                        Finalizado em: {{ recordingEndTime }}
                      </div>
                    </v-card-text>
                  </v-card>
                  </div>
                </v-col>
              </v-row>

            </v-card-text>
          </v-card>

          <!--
            Bloco: Comandos ditos na consulta (salvia-861/862).

            Fica logo ABAIXO das anotacoes de proposito: a proposta altera o
            prontuario, e aprovar sem enxergar onde o dado cai e o oposto do que
            a validacao humana existe para garantir. Como modal, tapava
            exatamente a tela que precisa ser lida.
          -->
          <ComandosDeVozPainel
            ref="commandsSection"
            :comandos="voiceCommands"
            :transcricao="finalTranscript"
            :patient-id="patientId"
            @revisados="voiceCommands = $event"
          />

          <!-- Bloco: Resumo Estruturado -->
          <v-card
            ref="summarySection"
            outlined
            class="ds-section-card mb-4"
          >
            <v-card-title class="ds-section-card-title">
              <v-icon small color="primary" class="mr-2"
                >mdi-tag-outline</v-icon
              >
              <span>Resumo Estruturado</span>
            </v-card-title>
            <v-card-text class="pa-3">
              <!-- Resumo Estruturado -->
              <v-card outlined class="mt-3 pa-3" color="blue-grey lighten-5">
                <div
                  class="d-flex align-center justify-end mb-2"
                  style="min-height: 24px"
                >
                  <!--
                    salvia-841 (Vidal, T41/IT-15): duas abas, e não três.
                    salvia-937: a resumida vem em tópicos da IA (short_summary),
                    com fallback derivado da completa (utils/consultationSummary).
                    Some durante a edição — edita-se sempre a completa.
                  -->
                  <div
                    v-if="
                      structuredSummaryRaw &&
                      !loadingStructuredSummary &&
                      !editingSummary
                    "
                    class="resumo-abas mr-auto"
                    role="tablist"
                    aria-label="Versão do resumo"
                  >
                    <button
                      v-for="tab in summaryTabs"
                      :key="tab.value"
                      type="button"
                      role="tab"
                      class="resumo-aba"
                      :class="{ 'resumo-aba--ativa': summaryTab === tab.value }"
                      :aria-selected="summaryTab === tab.value"
                      @click="summaryTab = tab.value"
                    >
                      <v-icon x-small class="mr-1">{{ tab.icon }}</v-icon>
                      {{ tab.label }}
                    </button>
                  </div>

                  <!-- Modo edição -->
                  <template v-if="editingSummary">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          x-small
                          icon
                          color="success"
                          v-bind="attrs"
                          v-on="on"
                          @click="saveEditSummary"
                        >
                          <v-icon small>mdi-check</v-icon>
                        </v-btn>
                      </template>
                      <span>Salvar edição</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          x-small
                          icon
                          v-bind="attrs"
                          v-on="on"
                          @click="cancelEditSummary"
                        >
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </template>
                      <span>Cancelar</span>
                    </v-tooltip>
                  </template>
                  <!-- Modo visualização -->
                  <template
                    v-else-if="
                      structuredSummaryRaw && !loadingStructuredSummary
                    "
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          x-small
                          icon
                          v-bind="attrs"
                          v-on="on"
                          @click="startEditSummary"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Editar resumo</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          x-small
                          icon
                          v-bind="attrs"
                          v-on="on"
                          @click="copyStructuredSummary"
                        >
                          <v-icon small>mdi-content-copy</v-icon>
                        </v-btn>
                      </template>
                      <span>Copiar resumo</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          x-small
                          icon
                          v-bind="attrs"
                          v-on="on"
                          @click="downloadStructuredSummary"
                        >
                          <v-icon small>mdi-download</v-icon>
                        </v-btn>
                      </template>
                      <span>Baixar resumo</span>
                    </v-tooltip>
                  </template>
                </div>

                <!-- Falha do resumo: aviso com nova tentativa. O texto do erro
                     NÃO entra no resumo (antes entrava e era salvo na consulta). -->
                <div
                  v-if="summaryError && !loadingStructuredSummary"
                  class="summary-error mb-2"
                >
                  <v-icon small color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
                  <span>{{ summaryError }}</span>
                  <v-spacer></v-spacer>
                  <v-btn x-small depressed color="primary" @click="generateStructuredSummary">
                    <v-icon x-small left>mdi-refresh</v-icon>
                    Tentar de novo
                  </v-btn>
                </div>

                <div v-if="loadingStructuredSummary" class="text-center py-2">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="24"
                  ></v-progress-circular>
                  <p class="text-caption mt-2 ma-0">
                    Gerando resumo estruturado...
                  </p>
                </div>

                <!-- Modo edição: textarea com o texto bruto -->
                <v-textarea
                  v-else-if="editingSummary"
                  v-model="summaryDraft"
                  outlined
                  dense
                  auto-grow
                  rows="10"
                  hide-details
                  placeholder="Edite o resumo... Use '1. Título', '2. Título' para manter a formatação em seções."
                  background-color="white"
                ></v-textarea>

                <p
                  v-else-if="!structuredSummaryRaw && !summaryError"
                  class="text-caption text--secondary ma-0"
                >
                  {{
                    editMode
                      ? "Resumo não disponível."
                      : "O resumo estruturado aparecerá aqui após finalizar a transcrição."
                  }}
                </p>

                <p
                  v-else-if="summaryTab === 'short' && !shortSummaryRaw"
                  class="text-caption text--secondary ma-0"
                >
                  Nenhuma seção com conteúdo — a consulta não registrou pontos
                  relevantes. Veja a versão completa.
                </p>

                <!--
                  SALVIA-881: cada frase do resumo leva ao trecho da transcrição
                  (clique ou Enter). Ver utils/transcriptLink.
                -->
                <template v-else>
                <p v-if="canLinkToTranscript" class="text-caption text--secondary mb-2">
                  Clique numa frase para ver o trecho da conversa.
                </p>
                <div
                  ref="summaryBody"
                  class="text-body-2"
                  :class="{ 'summary-linkable': canLinkToTranscript }"
                  v-html="activeTabSummaryHtml"
                  @click="openTranscriptPassage"
                  @keydown.enter.prevent="openTranscriptPassage"
                ></div>
                </template>
              </v-card>
            </v-card-text>
          </v-card>
        </v-form>
      </v-card-text>
      <v-card-actions class="form-footer">
        <v-btn outlined color="grey darken-1" @click="requestClose">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>

        <v-spacer class="footer-spacer"></v-spacer>

        <!-- New Schedule Button -->
        <v-btn
          outlined
          color="secondary"
          class="mr-4"
          @click="$emit('open-schedule-dialog')"
        >
          <v-icon left>mdi-calendar-plus</v-icon>
          Agendar Retorno
        </v-btn>

        <!-- Mensagem Pos-Consulta (Pauta Inteligente / infratech) -->
        <v-btn
          outlined
          color="primary"
          class="mr-4"
          :disabled="!canGeneratePostConsultationEmail"
          @click="postConsultationEmailDialog = true"
        >
          <v-icon left>mdi-email-edit-outline</v-icon>
          Mensagem Pos-Consulta
        </v-btn>

        <!--
          Enquanto a consulta está sendo gravada, este botão encerra a
          gravação em vez de salvar: parar já gera o resumo estruturado, e
          salvar antes disso guardaria a consulta sem ele. Terminada a
          gravação, volta a ser o botão de salvar.

          salvia-879: o rótulo é "PARAR GRAVAÇÃO". O vocabulário do ATO passou a
          ser gravar; "transcrição" segue nomeando o TEXTO que sai disso, nos
          botões de editar, copiar e baixar logo acima.
        -->
        <v-btn
          data-tour="ad-salvar"
          :color="audioIsRecording ? 'error' : 'primary'"
          :disabled="audioIsRecording ? false : !hasDataToSave"
          :loading="isSaving"
          @click="audioIsRecording ? stopTranscription() : saveAll()"
        >
          <v-icon left>
            {{ audioIsRecording ? "mdi-stop-circle-outline" : "mdi-content-save" }}
          </v-icon>
          {{ primaryButtonLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <PostConsultationEmailDialog
      v-model="postConsultationEmailDialog"
      :payload="postConsultationEmailPayload"
      :patient-id="patientId"
    />

  </div>
</template>

<script>
import http from "@/services/http";
// REMOVIDO: import axios from "axios";
// REMOVIDO: import { checkMyUserId } from "@/services/authServices";

import { useSnackbar } from "@/composables/useSnackbar";
import { escapeHtml } from "@/utils/sanitizeHtml";
import {
  formatSummaryHtml,
  htmlToNumberedText,
  shortSummaryToDisplay,
  isEmptySection,
  parseSummarySections,
} from "@/features/patients/appointments/utils/consultationSummary";
import {
  bestMatchingSentence,
  findTranscriptPassage,
  noteMarker,
  removeNoteMarker,
  transcriptHtml,
} from "@/features/patients/appointments/utils/transcriptLink";
import {
  carregarRascunho,
  limparRascunho,
  salvarRascunho,
} from "@/features/patients/appointments/utils/rascunhoConsultaStore";
import AudioRecorder from "@/features/patients/appointments/AppointmentAudioRecorder.vue";
// Dialogs de inserção rápida — abertos dinamicamente conforme o manifesto do card.
import PostConsultationEmailDialog from "@/features/patients/appointments/PostConsultationEmailDialog.vue";
import ComandosDeVozPainel from "@/features/patients/appointments/ComandosDeVozPainel.vue";
import ProposalCard from "@/features/assistant/ProposalCard.vue";
import { comandosDaConsulta } from "@/services/assistantService";
import {
  carregarComandos,
  salvarComandos,
  limparComandos,
} from "@/features/patients/appointments/utils/comandosDeVozStore";
import tourDialogMixin from "@/features/tour/tourDialogMixin";
import { formatDateTime, toDateISO, todayISO, userTimeZone } from "@/utils/datetime";

export default {
  name: "AppointmentDialog",
  mixins: [tourDialogMixin],
  components: {
    AudioRecorder,
    PostConsultationEmailDialog,
    ComandosDeVozPainel,
    ProposalCard,
  },
  props: {
    appointmentId: {
      type: [Number, String],
      default: null,
    },
    // Histórico do kanban: o evento da agenda de onde este atendimento nasceu
    // ("Iniciar atendimento" e o aviso dos 5 minutos mandam o id). Sem ele,
    // vale a regra de antes: o agendamento do paciente hoje.
    sourceEventId: {
      type: [Number, String],
      default: null,
    },
  },
  setup() {
    const { success, warning, info, error, erroDeRequisicao } = useSnackbar();
    return { success, warning, info, error, erroDeRequisicao };
  },
  data() {
    return {
      editMode: false,
      isLoading: false,
      // salvia-860/861: comandos ditos com o gatilho "Salvia" na consulta.
      voiceCommands: [],
      // salvia-861 (22/09): propostas que nasceram no momento do comando ditado
      // e esperam aprovação nas Notas Salvas. Vivem no Laravel; isto é a cópia
      // que a tela mostra, atualizada pelo ProposalCard ao aprovar/recusar.
      instantProposals: [],
      preparingProposal: false,
      instantProposalError: "",
      // Comandos que NÃO viraram proposta na hora e foram adiados para a
      // varredura do fim. Existe para o adiamento não ser silencioso — ver
      // prepareInstantProposal.
      deferredCommands: [],
      isSaving: false,
      formValid: true,
      audioIsRecording: false,
      // salvia-839: 'in_person' | 'online' | null (sem agendamento hoje).
      scheduledMeetingType: null,
      // salvia-779: a consulta de hoje deste paciente, e o status que ela
      // tinha antes de o atendimento a mover para "Em Atendimento".
      todaysScheduledEvent: null,
      statusBeforeAppointment: null,
      appointmentSaved: false,
      headphoneWarningDismissed: false,
      recordingTime: 0,
      recordingEndTime: null,
      finalTranscript: "",
      interimTranscript: "",
      // SALVIA-881: trecho da transcrição aberto a partir de uma frase do resumo.
      transcriptPassage: null,
      // SALVIA-881: quando o resumo atual foi gerado, para dizer "esta nota é
      // posterior ao resumo" em vez de um seco "não encontrei".
      summaryGeneratedAt: null,
      structuredSummaryRaw: "",
      // salvia-937: a aba "Resumido" em tópicos, gerada junto com a completa e
      // gravada em `short_summary`. Vazia -> a tela deriva da completa.
      storedShortSummary: "",
      loadingStructuredSummary: false,
      // Falha do resumo: mensagem do aviso com "Tentar de novo" (vazio = ok).
      summaryError: "",
      // Cada pedido de resumo ganha um número; resposta de pedido antigo que
      // chegue depois do novo (pausas seguidas) é ignorada.
      summaryRequestSeq: 0,
      // Rascunho: só passa a gravar depois de decidida a recuperação.
      draftChecked: false,
      // Estados de edição in-place
      editingTranscript: false,
      editingSummary: false,
      // salvia-841: aba aberta do resumo ("full" = completa | "short" = resumida).
      summaryTab: "full",
      summaryTabs: [
        { value: "full", label: "Completo", icon: "mdi-text-box-outline" },
        { value: "short", label: "Resumido", icon: "mdi-text-short" },
      ],
      transcriptDraft: "",
      summaryDraft: "",
      openaiApiUrl: "/api/transcripts/summary",
      noteInput: "",
      notes: [],
      recordingStartTime: null,
      recordingEndTimeFormatted: null,
      appointmentDate: null,
      selectedReason: "",
      otherReasonText: "",
      // Verdadeiro quando o motivo foi puxado do agendamento do dia. O
      // card entao vira uma linha so; clicar em "da agenda" devolve o
      // seletor, para o medico poder corrigir.
      reasonFromSchedule: false,
      // SALVIA-929: o médico abriu o seletor para corrigir o motivo da agenda.
      reasonEditing: false,
      // O motivo como veio do agendamento — para mostrar se for trocado.
      scheduledReasonOriginal: "",
      // Observações do agendamento (só leitura).
      scheduledNotes: "",
      appointmentTypes: [
        { label: "Primeira consulta", value: "Primeira consulta" },
        {
          label: "Consulta de acompanhamento",
          value: "Consulta de acompanhamento",
        },
        { label: "Retorno", value: "Retorno" },
        { label: "Outra, especificar", value: "Outra" },
      ],
      patientId: null,
      patientGender: null,
      patientName: null,
      postConsultationEmailDialog: false,
      originalStateJson: "",
    };
  },
  computed: {
    /**
     * salvia-839. Tres condicoes, nesta ordem: so enquanto grava, so em
     * consulta online, e so enquanto o medico nao dispensar.
     *
     * `scheduledMeetingType` nula (paciente sem agendamento hoje, ou agenda
     * fora do ar) NAO mostra a tarja: na duvida, calar. Um aviso errado numa
     * consulta presencial ensina a ignorar o aviso certo na online.
     */
    showHeadphoneWarning() {
      return (
        this.audioIsRecording &&
        this.scheduledMeetingType === "online" &&
        !this.headphoneWarningDismissed
      );
    },

    /**
     * Copy literal do solicitante, com o nome no lugar do "Dr. XXX".
     *
     * O titulo as vezes ja vem digitado no proprio cadastro ("Dra. Ana" no
     * campo nome) — dai o recorte, senao a tarja sairia "Dr(a). Dra. Ana".
     * Sem nome cadastrado, trata sem nome em vez de deixar buraco no texto.
     */
    headphoneWarningText() {
      const rawName = String(this.$store.getters["auth/user"]?.name || "").trim();
      const nameWithoutTitle = rawName.replace(/^dr(?:a)?\.?\s+/i, "").trim();
      const salutation = nameWithoutTitle ? `Dr(a). ${nameWithoutTitle}` : "Doutor(a)";

      return `${salutation}, para que a transcrição funcione e consiga escutar o seu paciente, por favor, não use fone de ouvido`;
    },

    tourId() {
      return "dialog-atendimento";
    },
    formattedTime() {
      const hours = Math.floor(this.recordingTime / 3600);
      const minutes = Math.floor((this.recordingTime % 3600) / 60);
      const seconds = this.recordingTime % 60;
      if (hours > 0) {
        return `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
      }
      return `${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
    },
    hasDataToSave() {
      // Qualquer mudança em relação ao estado original ativa o botão
      return JSON.stringify(this.getCurrentState()) !== this.originalStateJson;
    },
    /**
     * O botão principal muda de papel durante a transcrição (salvia-XXX).
     * Parar é o passo que produz o resumo estruturado, então ele vem antes
     * de salvar — e o rótulo precisa dizer isso.
     */
    primaryButtonLabel() {
      if (this.audioIsRecording) return "PARAR GRAVAÇÃO";

      return "Salvar";
    },

    /** Motivo veio da agenda e ninguém pediu para alterar: card de uma linha. */
    reasonCollapsed() {
      return this.reasonFromSchedule && !this.reasonEditing;
    },

    reasonChangedFromSchedule() {
      return (
        this.reasonFromSchedule &&
        Boolean(this.scheduledReasonOriginal) &&
        this.finalReasonValue !== this.scheduledReasonOriginal
      );
    },

    finalReasonValue() {
      if (this.selectedReason === "Outra") {
        return this.otherReasonText.trim();
      }
      return this.selectedReason;
    },
    /**
     * salvia-841/937: a resumida. A gravada em tópicos quando existe; senão,
     * derivada da completa (consulta antiga ou completa recém-editada).
     */
    shortSummaryRaw() {
      return shortSummaryToDisplay(this.storedShortSummary, this.structuredSummaryRaw);
    },

    /** Texto numerado da aba aberta — o que se vê, copia e baixa. */
    activeTabSummaryRaw() {
      return this.summaryTab === "short"
        ? this.shortSummaryRaw
        : this.structuredSummaryRaw || "";
    },

    /**
     * HTML de EXIBIÇÃO da aba aberta. As frases viram links para a transcrição
     * (SALVIA-881) só aqui: o `structuredSummary` abaixo é o que se GRAVA, e
     * marcação de tela não pode ir para o banco.
     */
    activeTabSummaryHtml() {
      const raw = this.summaryTab === "short" ? this.shortSummaryRaw : this.structuredSummaryRaw;
      if (!raw) return "";
      return formatSummaryHtml(raw, { linkable: this.canLinkToTranscript, sentences: true });
    },

    /**
     * Há resumo com conteúdo para onde a nota possa levar? Um resumo que falhou
     * ("Erro ao gerar resumo…") ou só com seções vazias não conta.
     */
    hasSummary() {
      if (this.loadingStructuredSummary || this.editingSummary) return false;
      return parseSummarySections(this.structuredSummaryRaw).some((s) => !isEmptySection(s.content));
    },

    /** Há transcrição para onde levar? (SALVIA-881) */
    canLinkToTranscript() {
      return Boolean(this.finalTranscript && this.finalTranscript.trim()) && !this.editingTranscript;
    },

    /** O que vai para o rascunho da consulta (só no modo criação). */
    draftSnapshot() {
      return {
        transcricao: this.finalTranscript,
        notas: this.notes,
        motivo: this.selectedReason,
        outroMotivo: this.otherReasonText,
        resumo: this.structuredSummaryRaw,
        resumoTopicos: this.storedShortSummary,
      };
    },

    /** Para onde o clique na nota leva agora (dica ao passar o mouse). */
    noteLinkHint() {
      if (this.hasSummary) return "Ver no resumo";
      return this.canLinkToTranscript ? "Ver na transcrição" : "";
    },

    /** A transcrição escapada, com o trecho da frase clicada marcado. */
    transcriptDisplayHtml() {
      return transcriptHtml(this.finalTranscript, this.transcriptPassage);
    },

    structuredSummary() {
      // Renderiza HTML a partir do texto bruto (salvamos bruto, formatamos só na view)
      if (!this.structuredSummaryRaw) return "";
      return this.formatStructuredSummary(this.structuredSummaryRaw);
    },
    canGeneratePostConsultationEmail() {
      return Boolean(
        (this.structuredSummaryRaw && this.structuredSummaryRaw.trim()) ||
          (this.finalTranscript && this.finalTranscript.trim()) ||
          (this.notes && this.notes.length > 0),
      );
    },
    postConsultationEmailPayload() {
      if (!this.canGeneratePostConsultationEmail) return null;
      return {
        patient_name: this.patientName || null,
        consultation_reason: this.finalReasonValue || null,
        summary: this.structuredSummaryRaw || null,
        transcript: this.finalTranscript || null,
        notes: (this.notes || []).map((n) => n.content),
      };
    },
  },
  watch: {
    // Rascunho da consulta (rascunhoConsultaStore): guarda 1s depois da última
    // mudança, para o F5 no meio do atendimento não levar a transcrição.
    draftSnapshot() {
      // Enquanto o médico não responde "Recuperar?", o rascunho antigo não
      // pode ser sobrescrito pelo que a tela nova já começou a mostrar.
      if (this.editMode || !this.patientId || !this.draftChecked) return;
      clearTimeout(this._draftTimer);
      this._draftTimer = setTimeout(
        () => salvarRascunho(this.patientId, this.draftSnapshot),
        1000,
      );
    },
    // SALVIA-881: as posições do trecho valem para ESTE texto; se ele muda
    // (nova fala, edição), o destaque sai em vez de apontar o lugar errado.
    finalTranscript() {
      this.transcriptPassage = null;
    },
    appointmentId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.editMode = true;
          this.loadAppointmentData(newVal);
        } else {
          this.editMode = false;
          this.resetForm();
          this.$nextTick(() => this.setOriginalState());
        }
      },
    },
    /**
     * salvia-861: grava a cada mudanca, nao so ao fechar.
     *
     * O caso que isto cobre e o refresh no meio da consulta — e refresh nao avisa
     * antes. Salvar so na saida protegeria justamente a situacao que ja tinha
     * guarda, e deixaria de fora a que nao tem.
     *
     * Mora NESTE bloco `watch`. Ele ficava num segundo bloco `watch` mais abaixo
     * (f7bba05) e, num objeto JavaScript, a segunda chave apaga a primeira: o
     * observador de `appointmentId` sumia, e abrir um atendimento salvo nunca
     * carregava os dados dele — o diálogo abria vazio, como se fosse novo.
     */
    voiceCommands: {
      deep: true,
      handler(list) {
        if (!this.patientId) return;
        salvarComandos(this.patientId, list, this.appointmentId);
      },
    },
  },
  mounted() {
    // salvia-779: com o médico atendendo, o aviso "consulta começando" não abre
    // popup por cima — ele fica no sino, e o fim desta consulta oferece a próxima.
    this.$store.commit("atendimento/SET_EM_ATENDIMENTO", true);
    this.patientId = this.$route.params.id;
    // salvia-861: recupera o que foi ditado antes de um refresh.
    this.voiceCommands = carregarComandos(this.patientId, this.appointmentId);
    this.fetchPatientGender();
    this.loadScheduledEventData();
    window.addEventListener("beforeunload", this.onBeforeUnload);
    // Antes do tour: nada pode atrapalhar a recuperação do rascunho.
    this.offerDraftRestore();
    // O diálogo é montado (v-if) quando abre, então disparamos o tour aqui.
    this.tourOnOpen();
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    clearTimeout(this._draftTimer);
    this.$store.commit("atendimento/SET_EM_ATENDIMENTO", false);
    this.restoreStatusIfNotSaved();
  },
  methods: {
    /**
     * salvia-839: dispensar a tarja vale para AQUELA gravacao, nao para sempre.
     * Comecou a gravar de novo, o aviso volta — a escolha do fone tambem pode
     * ter mudado entre uma gravacao e outra.
     */
    onRecordingChange(recording) {
      this.audioIsRecording = recording;
      if (recording) this.headphoneWarningDismissed = false;
    },

    formatDateTime,

    /**
     * Consulta não salva desta paciente (a aba recarregou no meio do
     * atendimento): oferece trazer de volta. Só no modo criação.
     */
    async offerDraftRestore() {
      try {
        await this.askDraftRestore();
      } finally {
        this.draftChecked = true;
      }
    },

    async askDraftRestore() {
      if (this.editMode) return;
      const draft = carregarRascunho(this.patientId);
      if (!draft) return;

      const words = (draft.transcricao || "").trim().split(/\s+/).filter(Boolean).length;
      const notes = (draft.notas || []).length;
      const parts = [];
      if (words) parts.push(`transcrição com ${words} ${words === 1 ? "palavra" : "palavras"}`);
      if (notes) parts.push(`${notes} ${notes === 1 ? "nota" : "notas"}`);
      const at = new Date(draft.em).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

      const restore = await this.$confirm({
        type: "warning",
        title: "Recuperar consulta não salva?",
        message: `Esta paciente tem uma consulta que não foi salva (${parts.join(" e ")}, às ${at}).`,
        confirmText: "Recuperar",
        cancelText: "Descartar",
      });
      if (!restore) {
        limparRascunho(this.patientId);
        return;
      }

      this.finalTranscript = draft.transcricao || "";
      this.notes = draft.notas || [];
      if (draft.motivo) {
        this.selectedReason = draft.motivo;
        this.otherReasonText = draft.outroMotivo || "";
        // SALVIA-929: motivo do rascunho diferente do agendado não pode aparecer
        // como "do agendamento" — abre o seletor, que mostra "Agendado como".
        if (this.reasonFromSchedule && this.finalReasonValue !== this.scheduledReasonOriginal) {
          this.reasonEditing = true;
        }
      }
      this.structuredSummaryRaw = draft.resumo || "";
      this.storedShortSummary = draft.resumoTopicos || "";
      this.info("Consulta recuperada. Gravar de novo continua a transcrição.");
    },

    /** Fechar ou recarregar a aba gravando ou com alterações pede confirmação. */
    onBeforeUnload(event) {
      if (this.audioIsRecording || this.isDirty()) {
        event.preventDefault();
        event.returnValue = "";
      }
    },

    /**
     * salvia-861 (22/09): o profissional acabou de ditar um pedido de registro —
     * prepara a proposta AGORA, em vez de esperar o fim da consulta.
     *
     * Só chega aqui o que o detector classificou como pedido de registro
     * (`ehRegistro`); comentário sobre o sistema vira só chip. Ver
     * `gatilhoDeVoz.js`.
     *
     * MANDA A TRANSCRIÇÃO INTEIRA junto, não só o comando, e isso não é
     * desperdício: o comando quase nunca carrega a informação. "Anota o que ela
     * falou sobre o sono" se refere a uma conversa de minutos atrás — é nela que
     * o agente busca o conteúdo. O `comando_alvo` diz ao agente para resolver SÓ
     * este, sem devolver os anteriores.
     *
     * NÃO BLOQUEIA a consulta: o `await` é interno e a gravação segue. Se falhar,
     * o comando continua na lista de chips e a varredura do fim tenta de novo —
     * por isso o erro é informativo, não fatal.
     */
    async prepareInstantProposal(command) {
      if (!this.patientId || !command) return;

      // Sem transcrição ainda não há a que o comando se refere: "anota o que ela
      // falou sobre o sono" precisa da conversa, e ela pode não ter sido
      // transcrita até aqui. O comando não se perde — a varredura do fim o pega.
      //
      // ⚠️ MAS NÃO SAIR EM SILÊNCIO. Na validação em test de 22/09 isto mordeu:
      // o gravador já tinha avisado "preparando a proposta agora", e quando a
      // transcrição ainda estava vazia nada acontecia. A mensagem virava mentira
      // e o profissional ficava esperando um card que não vinha. Dizer "vou
      // deixar para o fim" custa uma linha; deixar ele descobrir depois custa a
      // confiança no recurso inteiro.
      if (!this.finalTranscript || !this.finalTranscript.trim()) {
        if (!this.deferredCommands.includes(command)) {
          this.deferredCommands.push(command);
        }
        return;
      }

      this.instantProposalError = "";
      this.preparingProposal = true;
      try {
        const r = await comandosDaConsulta(
          this.finalTranscript,
          this.patientId,
          command,
        );
        // O Laravel deduplica por payload, então um comando re-emitido pelo
        // reconhecedor devolve a proposta que já existe em vez de uma nova.
        // Ainda assim conferimos o id aqui: a lista da tela não pode ganhar o
        // mesmo card duas vezes.
        for (const proposal of r.proposals || []) {
          if (!this.instantProposals.some((p) => p.id === proposal.id)) {
            this.instantProposals.push(proposal);
          }
        }
        if (r.naoCriados && r.naoCriados.length) {
          this.instantProposalError = "Não consegui preparar essa agora.";
        }
        // Deu certo desta vez: tira do aviso de adiado, senão ele fica na tela
        // cobrando algo que já foi resolvido.
        this.deferredCommands = this.deferredCommands.filter((c) => c !== command);
      } catch (e) {
        this.instantProposalError =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          "Não consegui preparar essa agora.";
      } finally {
        this.preparingProposal = false;
      }
    },

    /** O ProposalCard devolve a proposta depois de aprovar, recusar ou editar. */
    onInstantProposalUpdate(updated) {
      this.instantProposals = this.instantProposals.map((p) =>
        p.id === updated.id ? updated : p,
      );
    },
    tourReady() {
      return !this.isLoading;
    },
    tourSteps() {
      return [
        {
          title: "Registro de Consulta",
          description:
            "Aqui você registra todo o atendimento do paciente: transcrição, motivo, anotações e informações clínicas. Vamos ver os principais recursos.",
        },
        {
          anchor: "ad-gravacao",
          title: "Transcrever a consulta",
          description:
            "Inicie a transcrição do áudio da consulta. Ao finalizar, a fala é transcrita automaticamente e um resumo estruturado é gerado para você.",
        },
        {
          anchor: "ad-motivo",
          title: "Motivo da consulta",
          description:
            'Selecione o motivo do atendimento (primeira consulta, retorno, etc.). Escolhendo "Outra", você poderá especificar livremente.',
        },
        {
          anchor: "ad-notas",
          title: "Anotações da evolução",
          description:
            "Digite observações e a evolução da consulta. Pressione Enter ou clique em Salvar Nota para adicioná-las à lista.",
        },
        {
          anchor: "ad-salvar",
          title: "Salvar a consulta",
          description:
            "Quando terminar, salve o atendimento. Tudo o que foi registrado ficará no histórico do paciente.",
        },
      ];
    },
    /**
     * Traz da agenda o que já foi combinado para este paciente hoje: o motivo
     * da consulta e a modalidade (presencial ou online).
     *
     * Quem marca a consulta na agenda já escolhe o tipo, e ele fica na
     * descrição do evento como "Tipo de consulta: X". Repetir a escolha ao
     * abrir o atendimento é retrabalho — e abre espaço para divergir do que
     * foi combinado com o paciente.
     *
     * O MOTIVO só é preenchido em atendimento NOVO: ao editar, ele vem do que
     * foi salvo e não pode ser sobrescrito. A MODALIDADE (salvia-839) é lida
     * sempre, porque não preenche campo nenhum — só decide se a tarja do fone
     * de ouvido aparece ao gravar, e gravar não depende de o atendimento ser
     * novo.
     *
     * Falha em silêncio de propósito — não achar o agendamento não pode
     * impedir o médico de atender.
     */
    async loadScheduledEventData() {
      if (!this.patientId) return;

      try {
        // Mesmo fuso que o kanban envia: "hoje" e o dia de quem acessa, nao
        // o do servidor (que roda em UTC). Sem isto, a partir das 21h os dois
        // discordariam -- a agenda mostraria a consulta e aqui o motivo viria
        // em branco, porque a busca cairia no dia seguinte.
        const { data } = await http.get("/api/events/today", {
          params: { time_zone: userTimeZone() },
        });
        if (!data?.success) return;

        // salvia-779: com duas consultas do paciente no dia (manhã atendida,
        // retorno à tarde), a que interessa é a que AINDA VAI acontecer — a
        // primeira da lista podia ser a já atendida, e o kanban não mexia.
        const patientEvents = (data.events || []).filter(
          (event) =>
            String(event.patient?.id) === String(this.patientId) &&
            event.status !== "canceled",
        );
        // Quem chegou por um agendamento específico atende ESSE — mesmo com
        // duas consultas do paciente no dia. E SÓ esse: se ele não é de hoje
        // (consulta futura aberta pela agenda), nenhum outro evento de hoje é
        // movido no kanban — senão a consulta ficaria ligada a um agendamento
        // e o card "Atendido" seria o de outro.
        let scheduledEvent = this.sourceEventId
          ? patientEvents.find((event) => String(event.id) === String(this.sourceEventId)) || null
          : patientEvents.find((event) =>
              ["pending", "waiting", "confirmed", "scheduled"].includes(event.status),
            ) || patientEvents[0];

        // SALVIA-929: aberto a partir de um agendamento de OUTRO dia (pela
        // agenda) — busca esse agendamento só para o motivo e as observações.
        // O kanban de hoje não é tocado (ver `fromOtherDay` abaixo).
        let fromOtherDay = false;
        if (this.sourceEventId && !scheduledEvent) {
          scheduledEvent = await this.fetchScheduledEvent(this.sourceEventId);
          fromOtherDay = Boolean(scheduledEvent);
        }

        // salvia-839. Vem ANTES de qualquer `return` por falta de tipo: um
        // agendamento sem "Tipo de consulta:" na descrição continua sendo uma
        // consulta online, e a tarja continua valendo.
        this.scheduledMeetingType = scheduledEvent?.meeting_type ?? null;

        // Daqui para baixo é só o motivo, que não se sobrescreve ao editar.
        if (this.appointmentId) return;

        // salvia-779: atendimento NOVO de quem tem consulta hoje → o card vai
        // para "Em Atendimento" no kanban. Editar consulta antiga não mexe na
        // agenda de hoje.
        if (!fromOtherDay) {
          this.todaysScheduledEvent = scheduledEvent || null;
          this.markInProgress();
        }

        // SALVIA-929: observações do agendamento, só para leitura.
        this.scheduledNotes = this.extractScheduleNotes(scheduledEvent?.description);

        const type = this.extractAppointmentType(scheduledEvent?.description);
        if (!type) return;

        const isKnown = this.appointmentTypes.some((t) => t.value === type);

        if (isKnown) {
          this.selectedReason = type;
        } else {
          // Tipo digitado à mão no agendamento: entra como "Outra".
          this.selectedReason = "Outra";
          this.otherReasonText = type;
        }

        this.scheduledReasonOriginal = type;
        this.reasonFromSchedule = true;
      } catch (err) {
        console.warn("Não foi possível ler o motivo do agendamento:", err);
      }
    },

    /**
     * Cria a consulta ligada ao evento da agenda de onde ela nasceu — é a
     * ligação que o histórico do kanban usa para o "Abrir consulta".
     *
     * O evento vem do id da rota ou, sem ele, do agendamento de hoje achado em
     * `loadScheduledEventData`. O backend só aceita o vínculo se o evento for
     * deste médico com este paciente; se recusar SÓ isso (422 em `event_id`),
     * a consulta é salva sem o vínculo. Perder o registro clínico por causa de
     * um atalho de navegação seria muito pior do que ficar sem o atalho.
     */
    async createAppointment(payload) {
      const eventId = this.sourceEventId || this.todaysScheduledEvent?.id || null;
      if (!eventId) return http.post("/api/appointments/create", payload);

      try {
        return await http.post("/api/appointments/create", { ...payload, event_id: eventId });
      } catch (e) {
        const rejectedOnlyTheLink =
          e?.response?.status === 422 && e?.response?.data?.errors?.event_id;
        if (!rejectedOnlyTheLink) throw e;

        console.warn("[atendimento] vínculo com a agenda recusado; salvando sem ele.");
        return http.post("/api/appointments/create", payload);
      }
    },

    /**
     * Lê "Tipo de consulta: X" da descrição do evento.
     *
     * O agendamento monta a descrição como
     * `Tipo de consulta: X - Observações: Y`, então o corte no hífen isola o
     * tipo. Mesmo formato que o kanban da agenda já lê.
     */
    /**
     * SALVIA-929: um agendamento pelo id (GET /events/{id}/card), no mesmo
     * formato do card do dia. Só vale se for deste paciente; falha em silêncio,
     * como o resto da leitura da agenda — não achar não impede atender.
     */
    async fetchScheduledEvent(eventId) {
      try {
        const { data } = await http.get(`/api/events/${eventId}/card`);
        const event = data?.event;
        return event && String(event.patient?.id) === String(this.patientId) ? event : null;
      } catch {
        return null;
      }
    },

    /** "Tipo de consulta: X - Observações: Y" → "Y" (o agendamento grava assim). */
    extractScheduleNotes(description) {
      const found = String(description || "").match(/Observa[çc][õo]es:\s*([\s\S]+)$/);
      return found ? found[1].trim() : "";
    },

    /** SALVIA-929: desfaz a troca — volta ao motivo como veio do agendamento. */
    restoreScheduledReason() {
      const type = this.scheduledReasonOriginal;
      if (!type) return;
      if (this.appointmentTypes.some((t) => t.value === type)) {
        this.selectedReason = type;
        this.otherReasonText = "";
      } else {
        this.selectedReason = "Outra";
        this.otherReasonText = type;
      }
      this.reasonEditing = false;
    },

    extractAppointmentType(description) {
      // Para no separador " - Observações:" (o formato do agendamento), não
      // no primeiro hífen: "Avaliação pós-operatória" virava "Avaliação pós".
      const found = String(description || "").match(
        /Tipo de consulta:\s*(.+?)(?:\s+-\s+Observa[çc][õo]es:|\n|$)/,
      );

      return found ? found[1].trim() : "";
    },

    async fetchPatientGender() {
      if (!this.patientId) return;
      try {
        const { data } = await http.get(
          `/api/relation/get-patient-by-id/${this.patientId}`,
        );
        if (data?.status === "success") {
          this.patientGender = data.data?.gender || null;
          this.patientName =
            data.data?.name ||
            data.data?.full_name ||
            data.data?.first_name ||
            null;
        }
      } catch (err) {
        console.error("Erro ao buscar dados do paciente:", err);
      }
    },

    async loadAppointmentData(id) {
      this.isLoading = true;
      try {
        // axios -> http (baseURL + bearer automático)
        const { data: response } = await http.get(`/api/appointments/${id}`);
        const appointment = response.data;
        this.appointmentDate = appointment.appointment_date;
        this.recordingStartTime = appointment.start_time;
        this.recordingEndTimeFormatted = appointment.end_time;
        this.finalTranscript = appointment.transcription || "";
        // Compatibilidade: se vier HTML antigo salvo no banco, converte para texto simples
        const rawSummary = appointment.structured_summary || "";
        this.structuredSummaryRaw = this.isHtml(rawSummary)
          ? this.htmlToPlainText(rawSummary)
          : rawSummary;
        this.storedShortSummary = appointment.short_summary || "";
        const reasonMatch = this.appointmentTypes.find(
          (type) => type.value === appointment.reason,
        );
        if (reasonMatch) {
          this.selectedReason = appointment.reason;
        } else if (appointment.reason) {
          this.selectedReason = "Outra";
          this.otherReasonText = appointment.reason;
        }
        if (appointment.notes && appointment.notes.length > 0) {
          this.notes = appointment.notes.map((note) => ({
            id: note.id,
            content: note.content,
            timestamp: this.formatDateTime(note.created_at),
            is_private: note.is_private,
          }));
        }
        this.info("Dados da consulta carregados");
        this.$nextTick(() => this.setOriginalState());
      } catch (err) {
        console.error("Erro ao carregar consulta:", err);
        this.erroDeRequisicao(err, "Erro ao carregar dados da consulta");
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.finalTranscript = "";
      this.interimTranscript = "";
      this.structuredSummaryRaw = "";
      this.editingTranscript = false;
      this.editingSummary = false;
      this.transcriptDraft = "";
      this.summaryDraft = "";
      this.selectedReason = "";
      this.otherReasonText = "";
      this.reasonFromSchedule = false;
      this.reasonEditing = false;
      this.scheduledReasonOriginal = "";
      this.scheduledNotes = "";
      this.notes = [];
      this.noteInput = "";
      this.recordingTime = 0;
      this.recordingEndTime = null;
      this.recordingStartTime = null;
      this.recordingEndTimeFormatted = null;
      this.$nextTick(() => {
        if (this.$refs.appointmentForm) {
          this.$refs.appointmentForm.resetValidation();
        }
      });
    },
    /**
     * salvia-779: as três transições que o atendimento faz na agenda.
     *
     * Usam o MESMO endpoint do arrastar no kanban, que já autoriza e agora
     * avisa o kanban em tempo real (EventStatusChanged). Nenhuma delas pode
     * impedir a consulta: o registro clínico vale mais que a coluna do card,
     * então falha aqui só vira log.
     */
    async markInProgress() {
      const scheduledEvent = this.todaysScheduledEvent;
      if (!scheduledEvent?.id) return;
      if (!["scheduled", "confirmed", "waiting"].includes(scheduledEvent.status)) return;

      // Anotado ANTES da chamada: se o médico fechar o diálogo enquanto ela
      // ainda corre, o `beforeDestroy` precisa saber para onde devolver o card.
      // Anotado depois, o card ficava preso em "Em Atendimento".
      this.statusBeforeAppointment = scheduledEvent.status;

      try {
        await http.put(`/api/events/${scheduledEvent.id}/status`, { status: "pending" });
      } catch (e) {
        this.statusBeforeAppointment = null;
        console.warn("[salvia-779] não marcou Em Atendimento:", e);
      }
    },

    async completeScheduledEvent() {
      const scheduledEvent = this.todaysScheduledEvent;
      if (!scheduledEvent?.id) return;
      // Só conclui o que este atendimento abriu, ou o que já estava em
      // atendimento. Consulta que alguém marcou como Falta não vira Atendida.
      if (!this.statusBeforeAppointment && scheduledEvent.status !== "pending") return;

      try {
        await http.put(`/api/events/${scheduledEvent.id}/status`, { status: "concluded" });
      } catch (e) {
        console.warn("[salvia-779] não marcou Atendidos:", e);
      }
    },

    /**
     * Fechou sem salvar: a consulta não aconteceu, então o card volta para onde
     * estava. Um F5 no meio do atendimento não passa por aqui — o card fica em
     * "Em Atendimento" até alguém arrastá-lo ou o atendimento ser salvo.
     */
    restoreStatusIfNotSaved() {
      const scheduledEvent = this.todaysScheduledEvent;
      const previousStatus = this.statusBeforeAppointment;
      // Salvou e só o "Atendidos" falhou: a consulta ACONTECEU. Devolver o card
      // para "Agendado" diria o contrário — melhor ficar em "Em Atendimento".
      if (!scheduledEvent?.id || !previousStatus || this.appointmentSaved) return;

      http
        .put(`/api/events/${scheduledEvent.id}/status`, { status: previousStatus })
        .catch((e) => console.warn("[salvia-779] não devolveu o status:", e));
    },

    closeOrOpenMenu() {
      // Parar a transcrição se estiver ativa ao fechar
      if (this.$refs.audioRecorder && this.$refs.audioRecorder.isRecording) {
        this.$refs.audioRecorder.stopRecording();
      }
      this.$emit("closeRightMenu");
    },
    /**
     * Encerra a transcrição em curso.
     *
     * Não gera o resumo aqui: parar dispara `recording-stopped`, e é o
     * handleRecordingStopped que cuida disso — um caminho só, igual ao de
     * quem para pelo próprio gravador.
     */
    stopTranscription() {
      const recorder = this.$refs.audioRecorder;

      if (recorder && recorder.isRecording) {
        recorder.stopRecording();
      }

      // Parar é o que produz o resumo, e ele fica muito abaixo da dobra: sem
      // isto o médico aperta o botão e a tela não mexe, como se nada tivesse
      // acontecido. Rolar até lá mostra o "Gerando resumo..." e deixa claro
      // que o trabalho começou.
      this.scrollToSummary();
    },

    scrollToSummary() {
      this.scrollToSection("summarySection");
    },

    /**
     * Leva o medico ate o painel de comandos ditos (salvia-861/862).
     *
     * Ele fica abaixo das anotacoes, entao o contador no cabecalho e a guarda
     * de fechamento precisam ROLAR ate la — antes abriam um modal, que e o que
     * acabou de deixar de existir.
     */
    goToCommands() {
      this.scrollToSection("commandsSection");
    },

    scrollToSection(refName) {
      this.$nextTick(() => {
        const section = this.$refs[refName];
        const el = section && (section.$el || section);

        if (!el || typeof el.scrollIntoView !== "function") {
          return;
        }

        // Quem pediu menos animação no sistema operacional recebe o salto seco.
        const reduceMotion = window.matchMedia
          ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
          : false;

        el.scrollIntoView({
          behavior: reduceMotion ? "auto" : "smooth",
          block: "start",
        });
      });
    },

    handleRecordingStopped({
      transcription,
      endTime,
      startTime,
      endTimeFormatted,
    }) {
      this.finalTranscript = transcription;
      this.recordingEndTime = endTime;
      this.recordingStartTime = startTime;
      this.recordingEndTimeFormatted = endTimeFormatted;

      if (this.finalTranscript.trim()) {
        this.generateStructuredSummary();
      } else {
        this.warning("Nenhuma transcrição disponível para resumo");
      }
    },

    handleRecordingPaused({ transcription }) {
      this.finalTranscript = transcription;

      if (this.finalTranscript.trim()) {
        this.generateStructuredSummary();
      }
    },

    async generateStructuredSummary() {
      if (!this.finalTranscript.trim()) return;

      const seq = ++this.summaryRequestSeq;
      this.loadingStructuredSummary = true;
      this.summaryError = "";

      let generated = false;

      try {
        const { data } = await http.post("/api/transcripts/summary", {
          transcricao: this.finalTranscript,
        });
        if (seq !== this.summaryRequestSeq) return; // já há um pedido mais novo

        const summaryText = data.choices[0].message.content;
        this.structuredSummaryRaw = summaryText;
        // salvia-937: pode vir nulo (agente de tópicos falhou) — aí a aba
        // Resumido deriva da completa, como antes.
        this.storedShortSummary = data.resumo_topicos || "";
        this.summaryGeneratedAt = Date.now();
        this.success("Resumo estruturado gerado!");
        generated = true;
      } catch (err) {
        if (seq !== this.summaryRequestSeq) return;
        console.error("Erro ao gerar resumo:", err);
        console.error("Resposta:", err.response?.data);
        this.erroDeRequisicao(err, "Erro ao gerar resumo estruturado");
        // O resumo anterior (de uma pausa) continua valendo; sem nenhum, fica
        // vazio e a consulta pode ser salva assim — nunca com o texto do erro.
        this.summaryError = "Não foi possível gerar o resumo agora.";
      } finally {
        // Pedido antigo não mexe no "Gerando resumo..." do pedido novo.
        if (seq === this.summaryRequestSeq) {
          this.loadingStructuredSummary = false;

          // Rola DEPOIS de desligar o "Gerando resumo...", nao antes: enquanto
          // esse aviso esta na tela a secao e curta, e a rolagem pararia na
          // altura errada. Aqui o resumo inteiro ja esta montado, e alinhar o
          // topo faz a leitura comecar do comeco.
          if (generated) {
            this.scrollToSummary();
          }
        }
      }
    },

    /**
     * Monta o HTML do resumo a partir do texto bruto.
     *
     * O texto vem da transcrição resumida pela IA externa — conteúdo de fora,
     * gravado em `structured_summary` como `nullable|string`. Como ele era
     * interpolado direto no template, um `<img src=x onerror=...>` no resumo
     * executava ao abrir a consulta. Agora título e conteúdo passam por
     * `escapeHtml`: a MARCAÇÃO é nossa, o TEXTO é dado — e dado nunca vira tag.
     *
     * O estilo saiu do atributo `style` inline e virou classe: assim o
     * sanitizador da tela de listagem pode zerar todos os atributos sem que o
     * resumo perca a aparência.
     */
    /**
     * SALVIA-881 — clique (ou Enter) numa frase do resumo: acha o trecho da
     * conversa de onde ela saiu, marca e rola até ele.
     *
     * Quando não acha, diz isso. Apontar um trecho errado seria pior do que
     * não apontar nenhum: o médico confiaria no lugar indicado.
     */
    openTranscriptPassage(event) {
      const link = event.target?.closest?.(".summary-link");
      if (link) this.showTranscriptPassage(link.textContent);
    },

    /** Marca e mostra o trecho da transcrição de onde a frase do resumo saiu. */
    showTranscriptPassage(text) {
      if (!this.canLinkToTranscript) return;

      const passage = findTranscriptPassage(this.finalTranscript, text);
      if (!passage) {
        this.info("Não encontrei esse trecho na transcrição.");
        return;
      }

      this.transcriptPassage = passage;
      this.$nextTick(() => {
        const box = this.$refs.transcriptBox?.$el;
        const mark = box?.querySelector("mark.transcript-passage");
        // Um scrollIntoView só: ele rola a caixa da transcrição E o diálogo.
        (mark || box)?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },

    /**
     * SALVIA-881 — "Ver no resumo": desce até o Resumo Estruturado e faz piscar
     * a frase que corresponde à nota.
     *
     * Procura na aba aberta e, não achando na Resumida (que corta frases),
     * passa para a Completa. Não achando em nenhuma, diz POR QUÊ quando dá:
     * nota escrita depois do resumo ainda não tinha como estar nele.
     */
    /**
     * Clique na nota: a frase do resumo que a contém; não havendo, o ponto em
     * que ela entrou na transcrição; nenhum dos dois, diz por quê.
     */
    async openNote(note) {
      if (await this.showNoteInSummary(note)) return;
      if (this.showNoteInTranscript(note)) return;

      const afterSummary =
        note.createdAt && this.summaryGeneratedAt && note.createdAt > this.summaryGeneratedAt;
      if (this.hasSummary) {
        this.info(
          afterSummary
            ? "Esta nota foi escrita depois do resumo. Ela entra quando o resumo for gerado de novo."
            : "Não encontrei esta nota no resumo.",
        );
      } else {
        this.info("Esta nota foi escrita sem gravar. Ela aparece no resumo ao parar a gravação.");
      }
    },

    /** Destaca a marca `[Nota: …]` na transcrição. Devolve se achou. */
    showNoteInTranscript(note) {
      if (!this.canLinkToTranscript) return false;
      const marker = noteMarker(note.content);
      const start = this.finalTranscript.lastIndexOf(marker);
      if (start < 0) return false;

      this.transcriptPassage = {
        start,
        end: start + marker.length,
        matched: new Set(),
        label: "Nota destacada na transcrição",
      };
      this.$nextTick(() => {
        const box = this.$refs.transcriptBox?.$el;
        const mark = box?.querySelector("mark.transcript-passage");
        (mark || box)?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return true;
    },

    /** Leva à frase do resumo que contém a nota. Devolve se achou. */
    async showNoteInSummary(note) {
      if (!this.hasSummary) return false;

      let sentence = this.findSummarySentence(note.content);
      if (!sentence && this.summaryTab !== "full") {
        this.summaryTab = "full";
        await this.$nextTick();
        sentence = this.findSummarySentence(note.content);
      }

      if (!sentence) return false;

      this.flashSentence(sentence);
      return true;
    },

    /** A frase (`span.summary-sentence`) do resumo na tela que corresponde ao texto. */
    findSummarySentence(text) {
      const body = this.$refs.summaryBody;
      if (!body) return null;
      const spans = Array.from(body.querySelectorAll(".summary-sentence"));
      // O título da seção vai junto: "Alergias: dipirona" casa com "Dipirona."
      // dentro de "Alergias".
      const texts = spans.map((span) => {
        const heading = span.closest("p")?.previousElementSibling;
        const title = heading?.tagName === "H4" ? heading.textContent : "";
        return `${title} ${span.textContent}`;
      });
      const index = bestMatchingSentence(texts, text);
      return index >= 0 ? spans[index] : null;
    },

    /** Rola até a frase e a faz piscar (some sozinho; ver `.summary-sentence--flash`). */
    flashSentence(el) {
      const reduceMotion = window.matchMedia
        ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
        : false;
      el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });

      // Tirar e repor a classe reinicia a animação num segundo clique seguido.
      el.classList.remove("summary-sentence--flash");
      void el.offsetWidth;
      el.classList.add("summary-sentence--flash");
      // Sem animação (movimento reduzido) o destaque é fixo; sai no mesmo tempo.
      clearTimeout(el._flashTimer);
      el._flashTimer = setTimeout(() => el.classList.remove("summary-sentence--flash"), 2600);
    },

    formatStructuredSummary(text) {
      // salvia-841: o parse e a marcação moram em utils/consultationSummary, que
      // o histórico de consultas também usa — uma regra só para as duas telas.
      return formatSummaryHtml(text);
    },

    /**
     * Registra nas notas da consulta uma inserção feita pelo "+" de um card
     * do painel direito (salvia-781).
     *
     * Chamado pela PatientProfileDashboardPage, que é quem renderiza este
     * diálogo e os cards. É público de propósito: o card não conhece a
     * consulta, e a consulta não conhece o card.
     *
     * A nota fica só na lista em memória, como as digitadas à mão — quem
     * persiste é o salvamento da consulta.
     */
    registerQuickInsert({ title, items } = {}) {
      if (!title) return;

      const timestamp = new Date().toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });

      // Uma nota POR ITEM: escolher três alergias de uma vez vira três notas,
      // como ficaria se tivessem sido lançadas separadamente. Quando o card
      // grava algo que não dá para nomear, sobra uma nota genérica — que ao
      // menos registra que houve inserção e de qual card.
      const contents =
        Array.isArray(items) && items.length
          ? items.map((item) => `${title}: ${item}`)
          : [`${title}: informação registrada pelo atalho do card.`];

      // Mesmo esquema de id das notas digitadas neste arquivo. A parte
      // fracionária evita colidir com elas e entre si dentro do mesmo lote —
      // o id serve de :key da lista e de alvo da exclusão.
      const newNotes = contents.map((content) => ({
        id: Date.now() + Math.random(),
        createdAt: Date.now(),
        content,
        timestamp: timestamp,
        is_private: false,
      }));

      // Espalhado num único unshift para as notas do mesmo lançamento
      // aparecerem juntas e na ordem em que foram escolhidas.
      this.notes.unshift(...newNotes);
      newNotes.forEach((n) => this.addNoteToTranscript(n.content));

      this.success(
        newNotes.length > 1
          ? `${newNotes.length} registros adicionados às notas da consulta.`
          : `${title} adicionado às notas da consulta.`,
      );
    },

    saveNote() {
      if (!this.noteInput.trim()) return;

      const note = {
        id: Date.now(),
        createdAt: Date.now(),
        content: this.noteInput,
        timestamp: new Date().toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      this.notes.unshift(note);
      this.addNoteToTranscript(note.content);
      this.noteInput = "";
      this.success("Nota salva!");
    },

    /**
     * SALVIA-881: a nota entra na transcrição como `[Nota: …]`, no ponto atual
     * da conversa, e o clique nela leva a esse ponto exato.
     *
     * Só durante a gravação (pausada também conta): é quando existe "ponto da
     * conversa". Sem gravar, a nota não tem onde cair no texto e fica só na
     * lista. Quem guarda o texto gravando é o gravador (ele reemite tudo a
     * cada fala); devolve false quando não está gravando.
     */
    addNoteToTranscript(content) {
      this.$refs.audioRecorder?.insertNote?.(content);
    },

    /** Nota excluída: a marca dela sai da transcrição também. */
    removeNoteFromTranscript(content) {
      if (this.$refs.audioRecorder?.removeNote?.(content)) return;
      this.finalTranscript = removeNoteMarker(this.finalTranscript, content);
      if (this.editingTranscript) {
        this.transcriptDraft = removeNoteMarker(this.transcriptDraft, content);
      }
    },

    async deleteNote(noteId) {
      const ok = await this.$confirm({
        type: "delete",
        title: "Excluir nota",
        message: "Tem certeza que deseja excluir esta nota?",
        confirmText: "Excluir",
      });
      if (!ok) return;
      const removed = this.notes.find((n) => n.id === noteId);
      this.notes = this.notes.filter((n) => n.id !== noteId);
      if (removed) this.removeNoteFromTranscript(removed.content);
      this.info("Nota excluída");
    },

    /**
     * Monta o resumo em HTML pensado para COLAR FORA DO SALVIA — salvia-880.
     *
     * Nao reaproveita o `structuredSummary` da tela de proposito. Aquele HTML
     * carrega a aparencia em CLASSES (`summary-heading`, `summary-paragraph`),
     * que existem na folha de estilo desta tela e em lugar nenhum mais: colado
     * no Word, viraria texto corrido sem hierarquia. Aqui o estilo vai inline,
     * que e o que o Word importa, e o titulo e um `<h2>` de verdade — entao ele
     * aparece como titulo no documento e no painel de navegacao.
     *
     * As quebras internas do conteudo viram `<br>`: dentro de um `<p>` o HTML
     * colapsa `\n` em espaco, e era assim que a lista de itens de uma secao
     * chegava no Word como um paragrafo unico.
     *
     * O escape vem ANTES de inserir marcacao, nunca depois: o texto e conteudo
     * gerado por IA externa, e inverter a ordem reabriria a injecao que o
     * `formatStructuredSummary` fechou.
     */
    buildSummaryForClipboard(text = this.activeTabSummaryRaw) {
      const FONT = "Calibri, Arial, sans-serif";
      const sections = String(text ?? "").split(/(?=\d+\.\s)/);
      let html = "";

      sections.forEach((section) => {
        if (!section.trim()) return;

        const lines = section.trim().split("\n");
        const rawTitle = lines[0];
        if (!rawTitle.match(/^\d+\.\s/)) return;

        const title = escapeHtml(rawTitle.replace(/^\d+\.\s/, ""));
        html +=
          `<h2 style="font-family:${FONT};font-size:13pt;font-weight:bold;` +
          `margin:14pt 0 4pt 0">${title}</h2>`;

        const content = lines.slice(1).join("\n").trim();
        if (content) {
          const body = escapeHtml(content).replace(/\n+/g, "<br>");
          html +=
            `<p style="font-family:${FONT};font-size:11pt;line-height:1.4;` +
            `margin:0 0 8pt 0">${body}</p>`;
        }
      });

      return html;
    },

    /**
     * Copia o resumo com ESTRUTURA — salvia-880.
     *
     * Antes era `writeText(extractText(structuredSummary))`. Duas perdas numa
     * linha: o `extractText` desmonta o HTML que a tela acabou de montar, e o
     * `writeText` coloca na area de transferencia apenas `text/plain`, que e o
     * unico formato que o Word entao tem para ler.
     *
     * Pior que perder o negrito: o `extractText` e `body.textContent`, que
     * concatena os nos de texto SEM separador. Medido num resumo real de 9
     * secoes, o que era um texto de 25 quebras de linha chegava ao Word como
     * UM bloco unico, com o titulo colado no conteudo ("Queixa principal e
     * objetivo do pacienteNao foi mencionado na consulta.").
     *
     * Agora vao os dois formatos no mesmo item: o Word (e o Google Docs, e o
     * corpo de e-mail) le o `text/html` e recebe titulo e paragrafo de
     * verdade; quem so aceita texto — editor de codigo, campo de busca,
     * WhatsApp — cai no `text/plain`, que e o proprio texto numerado, com as
     * quebras preservadas.
     *
     * O caminho simples continua existindo como reserva: `ClipboardItem` nao
     * existe em todo navegador, e a escrita pode ser recusada sem gesto do
     * usuario. Falhar calado aqui seria pior do que nao ter o recurso, porque
     * a pessoa segue para o Word e cola o conteudo anterior da area de
     * transferencia sem perceber.
     */
    async copyStructuredSummary() {
      // salvia-841: copia a versão da aba aberta.
      const text = (this.activeTabSummaryRaw || "").trim();

      if (!text) {
        this.warning("Não há resumo para copiar.");
        return;
      }

      const html = this.buildSummaryForClipboard(text);
      const hasClipboardItem =
        typeof window.ClipboardItem === "function" &&
        typeof navigator.clipboard?.write === "function";

      if (html && hasClipboardItem) {
        try {
          await navigator.clipboard.write([
            new window.ClipboardItem({
              "text/html": new Blob([html], { type: "text/html" }),
              "text/plain": new Blob([text], { type: "text/plain" }),
            }),
          ]);
          this.success("Resumo copiado com a formatação!");
          return;
        } catch (e) {
          // Cai no caminho simples abaixo, que ainda copia o texto certo.
        }
      }

      try {
        await navigator.clipboard.writeText(text);
        this.success("Resumo copiado!");
      } catch (e) {
        this.error("Não foi possível copiar o resumo.");
      }
    },

    /**
     * salvia-880: o arquivo baixado sofria do mesmo defeito do copiar — passava
     * pelo `extractText`, que concatena os blocos sem separador, e o .txt saia
     * como um paragrafo unico.
     *
     * O texto certo ja existe aqui: `structuredSummaryRaw` e a versao numerada,
     * com as quebras de linha intactas. O formato do arquivo nao muda.
     */
    downloadStructuredSummary() {
      const isShort = this.summaryTab === "short";
      this.downloadText(
        this.activeTabSummaryRaw || "",
        isShort ? "resumo-resumido.txt" : "resumo-estruturado.txt",
      );
      this.success("Resumo baixado!");
    },

    copyTranscription() {
      navigator.clipboard.writeText(this.finalTranscript);
      this.success("Transcrição copiada!");
    },

    downloadTranscription() {
      this.downloadText(this.finalTranscript, "transcricao.txt");
      this.success("Transcrição baixada!");
    },

    downloadText(text, filename) {
      const element = document.createElement("a");
      const file = new Blob([text], { type: "text/plain" });
      element.href = URL.createObjectURL(file);
      element.download = filename;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    },

    async saveAll() {
      // Valida o formulário (motivo obrigatório, etc) - campos ficam vermelhos se inválidos
      if (!this.$refs.appointmentForm.validate()) {
        return;
      }

      this.isSaving = true;

      try {
        // 1. Parar a transcrição se estiver ativa
        if (this.$refs.audioRecorder && this.$refs.audioRecorder.isRecording) {
          this.$refs.audioRecorder.stopRecording();
          // Aguardar o evento recording-stopped ser processado
          await this.$nextTick();
          await new Promise((resolve) => setTimeout(resolve, 300));
        }

        // 2. Aguardar resumo estruturado se estiver sendo gerado
        if (this.loadingStructuredSummary) {
          await new Promise((resolve) => {
            const unwatch = this.$watch("loadingStructuredSummary", (val) => {
              if (!val) {
                unwatch();
                resolve();
              }
            });
          });
        }

        // 3. Se tem transcrição mas não tem resumo ainda, gerar e aguardar
        if (this.finalTranscript.trim() && !this.structuredSummaryRaw) {
          await this.generateStructuredSummary();
        }

        // 4. Salvar dados
        //
        // Usa o paciente que este diálogo CARREGOU (capturado no mounted), e
        // não o que estiver na URL agora. Os dois podem divergir: a rota
        // /patient-profile-dashboard/:id reaproveita o mesmo componente quando
        // só o parâmetro muda, então trocar o id na barra de endereço não
        // recarrega a tela — ela continua exibindo o paciente anterior.
        //
        // Lendo a URL aqui, o diálogo mostrava um paciente e gravava em outro.
        // Num prontuário isso é registro clínico no paciente errado.
        const patientId = this.patientId;

        // Se a URL mudou depois que o diálogo abriu, o contexto não é mais
        // confiável: melhor recusar e pedir para reabrir do que gravar com
        // base num palpite sobre qual dos dois o usuário quis.
        if (String(this.$route.params.id) !== String(this.patientId)) {
          this.$toast?.error?.(
            "O paciente da tela mudou. Feche e abra a consulta novamente.",
          );
          return;
        }

        // checkMyUserId() -> Vuex
        let doctorId = this.$store.getters["auth/userId"];

        // se ainda não carregou, tenta carregar
        if (!doctorId) {
          await this.$store.dispatch("auth/ensureAuth");
          doctorId = this.$store.getters["auth/userId"];
        }

        if (!doctorId) {
          throw new Error("Não foi possível identificar o usuário (doctorId).");
        }

        if (this.editMode) {
          const updateData = {
            patient_id: patientId,
            doctor_id: doctorId,
            appointment_date:
              this.appointmentDate || todayISO(),
            start_time: this.recordingStartTime || "00:00:00",
            end_time: this.recordingEndTimeFormatted || null,
            reason: this.finalReasonValue,
            transcription: this.finalTranscript || null,
            structured_summary: this.structuredSummary || null,
            short_summary: this.storedShortSummary || null,
            notes: this.notes.map((note) => ({
              id: note.id || null,
              content: note.content,
              is_private: note.is_private || false,
            })),
          };

          // axios.put -> http.put (sem headers manuais)
          await http.put(`/api/appointments/${this.appointmentId}`, updateData);

          this.success("Consulta atualizada com sucesso!");
        } else {
          const fullDate = new Date();

          const anamnesisData = {
            patient_id: patientId,
            doctor_id: doctorId,
            appointment_date: toDateISO(fullDate),
            start_time: this.recordingStartTime || "00:00:00",
            end_time: this.recordingEndTimeFormatted || null,
            reason: this.finalReasonValue,
            transcription: this.finalTranscript || null,
            structured_summary: this.structuredSummary || null,
            short_summary: this.storedShortSummary || null,
            notes: this.notes
              .map((note) => ({
                content: note.content,
                is_private: note.is_private || false,
              }))
              .filter((note) => note.content),
          };

          await this.createAppointment(anamnesisData);
          this.appointmentSaved = true;

          // salvia-779: a consulta da agenda vira "Atendidos" no kanban.
          await this.completeScheduledEvent();

          this.success("Consulta criada com sucesso!");
        }

        this.$emit("appointment-saved");
        this.setOriginalState();
        clearTimeout(this._draftTimer);
        limparRascunho(patientId);


        // Pequeno delay para o usuário ver o feedback de sucesso
        await new Promise((resolve) => setTimeout(resolve, 600));

        // salvia-779: lido ANTES de fechar — fechado, o componente some.
        const offerNext = !this.editMode;

        this.closeOrOpenMenu();

        // Com a consulta salva e o diálogo fechado, oferece a próxima do dia.
        // O popup é global (App.vue): não depende deste componente existir.
        if (offerNext) {
          this.$store.dispatch("atendimento/oferecerProxima", {
            patientIdAtual: patientId,
          });
        }
      } catch (err) {
        console.error("Erro ao salvar:", err);
        console.error("Resposta do erro:", err.response?.data);
        // salvia-798: mostra a referência de correlação junto do aviso, para o
        // time achar a exceção no log a partir do relato do usuário — sem
        // depender de reproduzir o cenário.
        this.erroDeRequisicao(
          err,
          `Erro ao ${this.editMode ? "atualizar" : "criar"} consulta`,
        );
      } finally {
        this.isSaving = false;
      }
    },

    getComparableNotes() {
      // evita timestamp/ids bagunçarem comparação
      return (this.notes || []).map((n) => ({
        id: n.id || null,
        content: (n.content || "").trim(),
        is_private: !!n.is_private,
      }));
    },

    getCurrentState() {
      return {
        editMode: !!this.editMode,
        selectedReason: this.selectedReason,
        otherReasonText: (this.otherReasonText || "").trim(),
        finalTranscript: (this.finalTranscript || "").trim(),
        structuredSummary: (this.structuredSummaryRaw || "").trim(),
        shortSummary: (this.storedShortSummary || "").trim(),
        notes: this.getComparableNotes(),
        appointmentId: this.appointmentId || null,
        audioIsRecording: this.audioIsRecording,
      };
    },

    setOriginalState() {
      this.originalStateJson = JSON.stringify(this.getCurrentState());
    },

    isDirty() {
      return JSON.stringify(this.getCurrentState()) !== this.originalStateJson;
    },

    // ===== Edição in-place: Transcrição =====
    startEditTranscript() {
      this.transcriptDraft = this.finalTranscript;
      this.editingTranscript = true;
    },
    saveEditTranscript() {
      this.finalTranscript = this.transcriptDraft;
      this.editingTranscript = false;
      this.success("Transcrição atualizada");
    },
    cancelEditTranscript() {
      this.transcriptDraft = "";
      this.editingTranscript = false;
    },

    // ===== Edição in-place: Resumo =====
    startEditSummary() {
      // A resumida é derivada: edita-se a completa, e ela se recalcula.
      this.summaryTab = "full";
      this.summaryDraft = this.structuredSummaryRaw;
      this.editingSummary = true;
    },
    saveEditSummary() {
      // salvia-937: a resumida gravada foi gerada a partir da completa ANTIGA.
      // Editou a completa, a resumida em tópicos deixa de valer e a aba volta
      // a derivar da completa até o resumo ser gerado de novo.
      if (this.summaryDraft !== this.structuredSummaryRaw) this.storedShortSummary = "";
      this.structuredSummaryRaw = this.summaryDraft;
      this.editingSummary = false;
      this.success("Resumo atualizado");
    },
    cancelEditSummary() {
      this.summaryDraft = "";
      this.editingSummary = false;
    },

    // ===== Helpers para migração de dados legados (HTML salvo no banco) =====
    isHtml(str) {
      return typeof str === "string" && /<\/?[a-z][\s\S]*>/i.test(str);
    },
    htmlToPlainText(html) {
      // Registros antigos guardaram HTML no lugar do texto bruto (ver util).
      return htmlToNumberedText(html);
    },

    async requestClose() {
      // salvia-861: comandos ditos e NAO revisados morrem com a tela.
      //
      // Eles vivem so no estado deste componente, que e montado com `v-if` — ao
      // fechar, somem. E o medico nao tem como saber: ele falou, viu o chip
      // aparecer, confiou, e perdeu. Perda silenciosa de coisa que a pessoa
      // acredita ter registrado e a mesma familia das salvia-810/823.
      //
      // Este aviso vem ANTES do "sair sem salvar" de propósito: os comandos se
      // perdem mesmo que a consulta seja salva, porque nao fazem parte do
      // formulario. Sao perdas independentes, e juntar as duas numa pergunta so
      // faria o medico responder a errada.
      if (this.voiceCommands.length) {
        const review = await this.$confirm({
          type: "warning",
          title: `${this.voiceCommands.length} comando(s) sem revisar`,
          message:
            "Você ditou comandos durante a consulta que ainda não viraram " +
            "propostas. Se sair agora, eles são descartados.",
          confirmText: "Revisar agora",
          cancelText: "Descartar",
        });
        if (review) {
          this.goToCommands();
          return;
        }

        // "Descartar" tem de descartar de verdade: sem isto os comandos voltam
        // ao reabrir a consulta, porque seguem gravados — e a tela teria
        // prometido uma coisa e feito outra.
        this.voiceCommands = [];
        limparComandos(this.patientId);
      }

      // Se abriu e nada mudou, fecha direto
      if (!this.isDirty()) {
        this.closeOrOpenMenu(); // emite closeRightMenu
        return;
      }

      // Se mudou, confirma saída
      const ok = await this.$confirm({
        type: "warning",
        title: "Sair sem salvar?",
        message:
          "Você tem alterações não salvas. Deseja realmente sair sem salvar?",
        confirmText: "Sair sem Salvar",
      });
      if (ok) {
        clearTimeout(this._draftTimer);
        limparRascunho(this.patientId);
        this.closeOrOpenMenu();
      }
    },
  },
};
</script>

<style scoped>
/* SALVIA-929: motivo vindo da agenda — origem e original preservado. */
.reason-origin {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--ds-text-muted);
}
.reason-notes {
  padding: 6px 10px 6px 30px;
  font-size: 12px;
  color: var(--ds-text-600);
  white-space: pre-line;
}
.reason-notes__label {
  font-weight: 600;
  color: var(--ds-text-soft);
}
.reason-restore {
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-primary);
  text-decoration: none;
}
.reason-restore:hover {
  text-decoration: underline;
}
.reason-original {
  margin-top: 6px;
  font-size: 12px;
  color: var(--ds-text-soft);
}

/* salvia-839: a tarja atravessa o dialogo de ponta a ponta (`tile` tira o
   arredondado) para nao parecer mais um cartao do formulario. Ela interrompe a
   leitura de proposito — e um aviso, nao um campo. */
.aviso-fone {
  border-radius: 0;
}
.aviso-fone__texto {
  line-height: 1.35;
}
/* O "x" nao pode competir com o texto: quem precisa do aviso le primeiro, quem
   ja sabe acha o fecho sem procurar. */
.aviso-fone__fechar {
  flex: 0 0 auto;
  margin-left: 8px;
  opacity: 0.85;
}
.aviso-fone__fechar:hover {
  opacity: 1;
}

/* ═══ Design system dialog header (mesmo de outros dialogs) ═══ */
.ds-dialog-header {
  background: linear-gradient(
    135deg,
    var(--ds-primary) 0%,
    var(--ds-primary-deep) 100%
  ) !important;
  color: var(--ds-white) !important;
  position: relative;
}

.ds-dialog-title {
  font-size: 16px !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px;
  color: var(--ds-white) !important;
}

.close-btn {
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.timer-badge {
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 999px;
  padding: 4px 14px;
}

.timer-text {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: var(--ds-white);
  font-variant-numeric: tabular-nums;
}

/* ═══ Sub-cards de seção dentro do dialog ═══
   Mesmo visual do .ds-form-section dos outros diálogos (salvia-dialogs.css):
   borda fina, canto 8, sem sombra e título numa faixa compacta. */
.ds-section-card {
  border-radius: 8px !important;
  border: 1px solid var(--ds-blue-100) !important;
  box-shadow: none !important;
  overflow: hidden;
}
.ds-section-card.mb-4 {
  margin-bottom: 10px !important;
}

.ds-section-card-title {
  background: var(--ds-blue-50);
  border-bottom: 1px solid var(--ds-blue-100);
  padding: 6px 10px !important;
  min-height: 32px;
  font-size: 10px !important;
  font-weight: 700 !important;
  line-height: 1.4 !important;
  color: var(--ds-primary) !important;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
}

/* Motivo sem agendamento: só o seletor, sem a folga de mensagem abaixo. */
.reason-body {
  padding: 8px 10px !important;
}

/* ═══ Notas | Transcrição — duas colunas da mesma altura ═══ */
.col-head {
  min-height: 24px;
}
.notes-col {
  display: flex;
  flex-direction: column;
}
.notes-panel {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--ds-surface-white);
  border: 1px solid var(--ds-blue-100);
  border-radius: 8px;
  overflow: hidden;
}
/* Base 0: quem dá a altura da linha é a transcrição; a lista fica com o resto. */
.notes-list {
  flex: 1 1 0;
  min-height: 96px;
  overflow-y: auto;
}
.notes-empty {
  margin: 0;
  padding: 16px 12px;
  text-align: center;
  font-size: 12px;
  color: var(--ds-text-soft);
}
.notes-composer {
  flex: 0 0 auto;
  padding: 8px;
  background: var(--ds-blue-50);
  border-top: 1px solid var(--ds-blue-100);
}
.notes-composer ::v-deep .v-input__slot {
  background: var(--ds-surface-white) !important;
}
/* Uma coluna só (tela estreita): a lista volta a ter teto próprio. */
@media (max-width: 959px) {
  .notes-list {
    flex: none;
    max-height: 256px;
  }
}
.transcript-box.v-card.v-sheet--outlined {
  border-color: var(--ds-blue-100);
  border-radius: 8px;
  background: var(--ds-surface-white);
}

.ds-inline-label {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  color: var(--ds-primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.transcription-text {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.recording-dot {
  width: 8px;
  height: 8px;
  background-color: var(--ds-red);
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

.recording-dot:nth-child(2) {
  animation-delay: 0.3s;
}

.recording-dot:nth-child(3) {
  animation-delay: 0.6s;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.3;
    transform: scale(0.8);
  }
}

.transcription-text {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.fixed-form-card {
  position: fixed;
  top: 16px;
  /* Preenche entre o drawer esquerdo e os cards da direita com gap de 16px */
  left: 316px; /* drawer(300px) + 16px gap */
  right: 372px; /* cards(340px) + margem(16px) + 16px gap — largura FIXA p/ alinhar */
  width: auto; /* auto para respeitar left+right */
  min-width: 320px; /* encolhe junto (sem transbordar sobre os cards) */
  z-index: 200;
  overflow: hidden;
  /*
    O card e PRESO ao viewport (top + bottom) e vira uma coluna flex.

    Antes so tinha `top`, e a altura vinha de `.form-content { max-height:
    calc(100vh - 200px) }` — um numero magico que assumia um rodape de altura
    fixa. Quando o rodape QUEBRA em duas linhas (o que acontece com o botao
    "PARAR GRAVACAO", mais largo que "Salvar"), o card passa de 100vh e a
    segunda linha cai abaixo da tela; como aqui tem `overflow: hidden`, nao da
    nem para rolar ate ela. Foi exatamente o que aconteceu em stage em 15/09.

    Com `bottom` + flex, quem encolhe e a area de conteudo, e o rodape fica
    sempre visivel — independentemente de quantas linhas ele ocupe.
  */
  bottom: 16px;
  display: flex;
  flex-direction: column;
  background: var(--ds-surface);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(31, 77, 63, 0.22), 0 6px 20px rgba(0, 0, 0, 0.08);
  animation: slideInLeft 0.3s ease-out;
}

@keyframes slideInLeft {
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* O v-card interno acompanha: coluna flex com `min-height: 0`, senao o filho
   que rola (.form-content) nao encolhe — em flexbox o minimo padrao e o
   conteudo, e sem isto o scroll simplesmente nao acontece. */
.fixed-form-card > .v-card {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

/* Rodapé de ações: quebra em várias linhas quando estreito, senão o botão
   Salvar fica cortado à direita pelo overflow:hidden do card. */
.form-footer {
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  /* Rodapé dos diálogos (.ds-dialog-actions): faixa clara com linha em cima. */
  padding: 10px 14px;
  background-color: var(--ds-blue-50);
  border-top: 1px solid var(--ds-blue-100);
}
.form-footer .v-btn {
  margin: 0 !important;
}
/* Em telas apertadas: sem spacer e botões compactos (cabem e quebram, sem cortar). */
@media (max-width: 1200px) {
  .form-footer .footer-spacer {
    display: none;
  }
  .form-footer .v-btn {
    font-size: 0.72rem;
    letter-spacing: 0;
    padding: 0 10px !important;
    min-width: 0 !important;
  }
  .form-footer .v-btn .v-icon {
    font-size: 16px !important;
  }
}

.form-content {
  /* A altura vem do flex do card, nao de um calc() que chuta o rodape. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  background: var(--ds-blue-bg-soft);
}

/* Scrollbar customizada */
.form-content::-webkit-scrollbar {
  width: 8px;
}

.form-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.form-content::-webkit-scrollbar-thumb {
  background: rgba(136, 136, 136, 0.5);
  border-radius: 4px;
}

.form-content::-webkit-scrollbar-thumb:hover {
  background: rgba(85, 85, 85, 0.8);
}

/* Responsividade — larguras FIXAS por faixa, alinhadas com a coluna de cards
   (o `right` do form = largura dos cards + gap). Mantém tudo lado a lado,
   só encolhendo, sem centralizar/flutuar. Bom em telas de MacBook (~1280–1512). */
@media (max-width: 1440px) {
  .fixed-form-card {
    right: 350px; /* cards 320 + 14 + 16 */
    max-width: none;
  }
}

@media (max-width: 1200px) {
  .fixed-form-card {
    right: 308px; /* cards 280 + 12 + 16 */
  }
}

@media (max-width: 900px) {
  .fixed-form-card {
    right: 262px; /* cards 240 + 10 + 12 */
    min-width: 220px;
  }
}

/* Responsividade — mobile (cards escondidos, form ocupa tudo) */
@media (max-width: 768px) {
  .fixed-form-card {
    left: 8px;
    right: 8px;
    top: 8px;
    bottom: 8px;
    transform: none;
    width: auto;
    max-width: none;
    border-radius: 8px;
  }

  /* Idem: o flex ja limita; o calc() aqui so recriava o bug em telas menores. */
}

@media (max-width: 480px) {
  .fixed-form-card {
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    border-radius: 0;
  }

  /* Idem: o flex ja limita; o calc() aqui so recriava o bug em telas menores. */
}

.timer-display {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background-color: var(--ds-blue-50);
  border: 1px solid var(--ds-blue-100);
  border-radius: 10px;
}
.notes-list-container,
.transcription-container {
  overflow-y: auto;
}
.notes-list-container::-webkit-scrollbar,
.transcription-container::-webkit-scrollbar {
  width: 6px;
}
.notes-list-container::-webkit-scrollbar-track,
.transcription-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.notes-list-container::-webkit-scrollbar-thumb,
.transcription-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}
.note-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.note-item:last-child {
  border-bottom: none;
}
.transcription-text {
  white-space: pre-wrap;
  line-height: 1.6;
  font-size: 0.875rem;
}
/* SALVIA-952: a caixa da transcrição tem altura fixa de 10 linhas (com o
   padding do v-card-text), vazia ou cheia; o excedente rola por dentro. A
   edição usa a mesma linha, então as 10 linhas são as mesmas nos dois modos. */
.transcript-box {
  height: calc(10 * 1.6 * 0.875rem + 32px);
  overflow-y: auto;
}
.transcript-edit ::v-deep textarea {
  line-height: 1.6;
  font-size: 0.875rem;
  overflow-y: auto;
}
.summary-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  font-size: 12px;
  color: var(--ds-danger);
  background: var(--ds-surface-white);
  border: 1px solid var(--ds-danger-light);
  border-radius: 8px;
}
.transcript-interim {
  color: var(--ds-text-soft);
  font-style: italic;
}
.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* ═══ Resumo estruturado ═══
   Estes estilos eram atributos `style` inline montados dentro do
   formatStructuredSummary. Viraram classes para que o saneamento possa zerar
   todos os atributos do HTML sem que o resumo perca a aparência. */
::v-deep .summary-heading {
  color: var(--ds-primary);
  margin-top: 12px;
  margin-bottom: 8px;
}
::v-deep .summary-paragraph {
  margin: 0 0 8px 0;
  /* Itens de lista vêm em linhas; sem isto viravam um parágrafo corrido. */
  white-space: pre-line;
}

/* salvia-841: seletor Completa / Resumida. */
.resumo-abas {
  display: inline-flex;
  padding: 2px;
  border-radius: 999px;
  background: var(--ds-surface-white, #fff);
  box-shadow: inset 0 0 0 1px var(--ds-primary-border, #cfe5da);
}
.resumo-aba {
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border: none;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-text-soft, #78909c);
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.resumo-aba .v-icon {
  color: inherit;
}
.resumo-aba:hover {
  color: var(--ds-primary-darker, #1f4d3f);
}
.resumo-aba--ativa {
  color: #fff;
  background: var(--ds-primary, #3e8e73);
}
.resumo-aba--ativa:hover {
  color: #fff;
}
::v-deep .summary-paragraph--muted {
  color: #757575;
  font-style: italic;
}

/* SALVIA-881: frases do resumo que levam ao trecho da transcrição */
::v-deep .summary-sentence {
  border-radius: 3px;
  transition: background-color 0.15s;
}
::v-deep .summary-link {
  cursor: pointer;
}
::v-deep .summary-link:hover,
::v-deep .summary-link:focus-visible {
  background-color: rgba(var(--v-primary-base-rgb, 60, 110, 90), 0.12);
  outline: none;
}
::v-deep mark.transcript-passage {
  background-color: rgba(255, 213, 79, 0.45);
  color: inherit;
  border-radius: 3px;
  padding: 1px 0;
}
::v-deep mark.transcript-passage strong {
  font-weight: 700;
}

.transcript-passage-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 0.8125rem;
}

/* SALVIA-881: frase do resumo que corresponde à nota — pisca e some */
::v-deep .summary-sentence--flash {
  animation: summary-sentence-flash 2.6s ease-out;
}
@keyframes summary-sentence-flash {
  0%,
  35% {
    background-color: rgba(255, 213, 79, 0.75);
    box-shadow: 0 0 0 2px rgba(255, 213, 79, 0.75);
  }
  100% {
    background-color: transparent;
    box-shadow: 0 0 0 2px transparent;
  }
}
@media (prefers-reduced-motion: reduce) {
  ::v-deep .summary-sentence--flash {
    animation: none;
    background-color: rgba(255, 213, 79, 0.45);
  }
}

/* SALVIA-881: a nota inteira leva ao resumo (ou à transcrição) */
.note-link {
  cursor: pointer;
  transition: background-color 0.15s;
}
.note-link + .note-link {
  border-top: 1px solid var(--ds-blue-100);
}
.note-link:hover,
.note-link:focus-visible {
  background-color: rgba(60, 110, 90, 0.08);
  outline: none;
}
</style>
